import { getFunctions, httpsCallable } from 'firebase/functions'
import { Coach } from 'fitify-types/src/types/coach'
import { WithId } from 'fitify-types/src/types/common'
import { UserCoach } from 'fitify-types/src/types/functions'
import useQuery from 'fitify-ui/src/hooks/useQuery'
import { toast } from 'fitify-ui-onboarding/src/components'
import { useUserContext } from 'fitify-ui-onboarding/src/contexts/UserContext'
import { getItem, setItem } from 'fitify-ui-onboarding/src/utils/storage'
import { useRouter } from 'next/router'
import { useCallback, useContext, useEffect, useState } from 'react'

import { updateCoachId, updateTopMatchCoachId } from 'api/userProfile'
import { LOGGED_IN_STORAGE_COACH_KEY, STORAGE_COACH_KEY } from 'utils/constants'
import { ONBOARDING_PAGES } from 'utils/routes'
import { DataContext } from 'utils/state'

type CoachData = Omit<WithId<Coach>, 'matching_preferences'>

export type CoachDataProps = {
  coachList: CoachData[]
  selectedCoach?: CoachData
  isLoadingCoachList: boolean
}

export const fetchCoaches = async () => {
  try {
    const getCoachList = httpsCallable<
      UserCoach.GetCoachList.Request,
      UserCoach.GetCoachList.Response
    >(getFunctions(), 'getCoachList')
    const coachRes = await getCoachList()
    return coachRes.data
  } catch (err) {
    console.error(err)
    toast({
      type: 'error',
      content: 'Failed to fetch coach list',
    })
  }

  return []
}

export const useCoaches = (isUsingStorage?: boolean): CoachDataProps => {
  const router = useRouter()
  const query = useQuery()
  const { data, set } = useContext(DataContext)

  const { userProfile, isLoggedIn } = useUserContext()

  const [isLoadingCoachList, setIsLoadingCoachList] = useState<boolean>(true)
  const [coachList, setCoachList] = useState<CoachData[]>([])
  const [selectedCoach, setSelectedCoach] = useState<CoachData>()

  const loadCoaches = useCallback(async () => {
    try {
      let coachesData
      if (isUsingStorage) {
        const coachStore = getItem(
          'session',
          isLoggedIn ? LOGGED_IN_STORAGE_COACH_KEY : STORAGE_COACH_KEY
        )
        const coachStoreData = coachStore ? JSON.parse(coachStore) : null
        coachesData = coachStoreData || (await fetchCoaches())
      } else {
        coachesData = await fetchCoaches()
      }

      if (coachesData && coachesData.length > 0) {
        setItem(
          'session',
          isLoggedIn ? LOGGED_IN_STORAGE_COACH_KEY : STORAGE_COACH_KEY,
          JSON.stringify(coachesData)
        )
        setCoachList(coachesData)
        if (isLoggedIn) {
          await updateTopMatchCoachId(coachesData[0].id)
        }
      }
    } catch {
      toast({
        type: 'error',
        content: 'Could not load coach list',
      })
    } finally {
      setIsLoadingCoachList(false)
    }
  }, [isLoggedIn, isUsingStorage])

  useEffect(() => {
    loadCoaches()
  }, [loadCoaches, isLoggedIn])

  useEffect(() => {
    if (!router.isReady || !coachList.length) return

    const subscriptionActive = userProfile?.subscription_hc?.active
    const userCoach = userProfile?.coach

    const queryCoachId = query?.coach_id as string | undefined
    const coachId: string | undefined =
      subscriptionActive && userCoach
        ? userCoach.coach_id
        : (queryCoachId ?? data.coach_id)

    const coach = coachList.find((coach) => coach.id === coachId)

    const isAvailabilityIgnored = query?.availability_ignored === '1'
    const isCoachAvailable = coach?.available

    if (coach) {
      if (!isCoachAvailable && !isAvailabilityIgnored) {
        const redirectUrl = isLoggedIn
          ? ONBOARDING_PAGES.selectCoach.path
          : ONBOARDING_PAGES.subscription.path

        if (isLoggedIn) updateCoachId(null)

        setSelectedCoach(undefined)
        set({ coach_id: undefined })
        router.replace(redirectUrl)
      } else {
        if (isLoggedIn) {
          updateCoachId(coach.id)
          set({ coach_id: coach.id })
        }

        setSelectedCoach(coach)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachList, router.isReady, userProfile, query])

  return {
    coachList,
    selectedCoach,
    isLoadingCoachList,
  }
}
